// 🔑 THE UNDERWRITING SUMMARY, STAFF DRAFT (Jesse, 2026-10-27 — the approved mockups, screen B). Every figure is read
// from the decision record (backend utils/uwSummary, GET /api/admin/borrowers/:id/uw-summary/model): line, calculation,
// figure and the stored field it comes from. Figures and calculations only, no prose; nothing is recomputed here.
// "Show the investor version" switches to the investor model (borrowers numbered, no field names).
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { T, card, label, btn } from './GuidedModuleSteps';

export default function UWSummaryPanel({ API, borrowerId, headers, refreshKey = 0 }) {
  const [variant, setVariant] = useState('staff');
  const [state, setState] = useState({ loading: true, model: null, error: null });
  useEffect(() => {
    let live = true;
    setState((s) => ({ ...s, loading: true }));
    axios.get(`${API}/api/admin/borrowers/${borrowerId}/uw-summary/model`, { headers, params: { variant } })
      .then((r) => { if (live) setState({ loading: false, model: r.data.model, error: null }); })
      .catch(() => { if (live) setState({ loading: false, model: null, error: 'The summary could not be read just now.' }); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API, borrowerId, variant, refreshKey]);
  const m = state.model;
  const staff = variant === 'staff';
  return (
    <section data-testid="uw-summary-panel" style={card()}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ display: 'grid', gap: 2 }}>
          <div style={label}>{m ? m.header.replace(/ · DLY-.*$/, '') : 'Underwriting summary'}{staff ? ' · staff draft' : ' · investor version'}</div>
          {staff && <span style={{ fontSize: 12, fontWeight: 800 }}>INTERNAL DRAFT, NOT FOR INVESTORS</span>}
        </div>
        <button type="button" data-testid="uw-summary-variant" style={btn('secondary')} onClick={() => setVariant(staff ? 'investor' : 'staff')}>
          {staff ? 'Show the investor version' : 'Show the staff draft'}
        </button>
      </div>
      {state.loading && !m ? <div style={{ fontSize: 13 }}>Reading the decision…</div> : null}
      {state.error ? <div style={{ fontSize: 13 }}>{state.error}</div> : null}
      {!state.loading && !m && !state.error ? <div data-testid="uw-summary-none" style={{ fontSize: 13 }}>No decision on record yet.</div> : null}
      {m && (
        <>
          <div style={{ fontSize: 13 }}>Every figure read from decision #{m.decisionId}. Figures and calculations only. A new decision regenerates it and retires this copy as replaced.</div>
          {m.missing.length > 0 && (
            <div data-testid="uw-summary-missing" style={{ fontSize: 13, background: T.tan, borderRadius: 8, padding: '10px 12px' }}>
              Not on this decision&apos;s record: {m.missing.join(', ')}. It was decided before the workings were stored; re-deciding the file adds them.
            </div>
          )}
          {m.sections.map((s) => (
            <div key={s.key} data-testid={`uw-summary-${s.key}`} style={{ display: 'grid', gap: 6, overflowX: 'auto' }}>
              <h3 style={{ margin: 0, fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: T.navy }}>{s.title}</h3>
              <table style={{ width: '100%', minWidth: staff ? 860 : 620, borderCollapse: 'collapse', fontSize: 13, fontVariantNumeric: 'tabular-nums', tableLayout: 'fixed' }}>
                <thead>
                  <tr>
                    {['Line', 'Calculation', 'Figure', ...(staff ? ['Stored field'] : [])].map((h, i) => (
                      <th key={h} style={{ textAlign: i === 2 ? 'right' : 'left', padding: '8px', borderBottom: `2px solid ${T.ink}`, fontSize: 12, width: staff ? ['27%', '38%', '13%', '22%'][i] : ['32%', '46%', '22%'][i] }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {s.rows.map((r, i) => (r.kind === 'sub' ? (
                    <tr key={i}><td colSpan={staff ? 4 : 3} style={{ padding: '14px 8px 8px', fontWeight: 800, borderBottom: `1px solid ${T.ink}` }}>{r.label}</td></tr>
                  ) : (
                    <tr key={i} data-kind={r.kind || ''}>
                      <td style={{ padding: 8, borderBottom: `1px solid ${T.line}`, borderTop: r.kind === 'total' ? `2px solid ${T.ink}` : undefined, fontWeight: r.kind === 'total' ? 800 : 400, verticalAlign: 'top' }}>{r.label}</td>
                      <td style={{ padding: 8, borderBottom: `1px solid ${T.line}`, borderTop: r.kind === 'total' ? `2px solid ${T.ink}` : undefined, verticalAlign: 'top' }}>{r.calc}</td>
                      <td style={{ padding: 8, borderBottom: `1px solid ${T.line}`, borderTop: r.kind === 'total' ? `2px solid ${T.ink}` : undefined, textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap', verticalAlign: 'top' }}>{r.figure}</td>
                      {staff && <td style={{ padding: 8, borderBottom: `1px solid ${T.line}`, borderTop: r.kind === 'total' ? `2px solid ${T.ink}` : undefined, font: '500 11.5px ui-monospace, Menlo, Consolas, monospace', wordBreak: 'break-word', verticalAlign: 'top' }}>{r.field || ''}</td>}
                    </tr>
                  )))}
                </tbody>
              </table>
            </div>
          ))}
        </>
      )}
    </section>
  );
}
